/**
 * useBiometria — desbloqueio do Meu Contador Marçal por Face ID / Touch ID / PIN.
 *
 * 16/06/2026 v2 — Sprint 2 Capacitor nativo.
 *
 * Detecta plataforma em runtime:
 *  - App nativo (iOS App Store / Play Store via Capacitor) → plugin
 *    `@aparajita/capacitor-biometric-auth` (compat Capacitor 6) com
 *    prompt SO nativo
 *  - PWA / browser → WebAuthn API (navigator.credentials)
 *
 * iOS WKWebView bloqueia WebAuthn (limitação Apple), por isso o
 * caminho nativo é obrigatório pro app empacotado. Em ambos os
 * modos, a interface pública é idêntica — telas não precisam saber.
 *
 * Modelo "local-only": sem ida ao backend, sem challenge/response real.
 * É uma trava de UX, não de segurança criptográfica. O JWT em
 * localStorage continua sendo a base; biometria apenas exige
 * consentimento físico do usuário pra recuperá-lo.
 *
 * Setup pro app nativo (Thiago, antes de rebuild pro App Store):
 *   cd frontend
 *   npm install                  # baixa @aparajita/capacitor-biometric-auth
 *   npm run build                # gera build/
 *   npx cap sync ios             # propaga plugin pro projeto Xcode
 *   npx cap open ios             # abre Xcode → Archive → Submit
 * (Android: trocar `ios` por `android`.)
 */
import { marcaGuardada as lerMarca } from '../../lib/semMarcal';

// App de marca: o aviso do sistema (biometria) leva o nome da marca, nunca o do Meu Contador.
const _nomeApp = () => (lerMarca() && lerMarca().nome_app) || 'Meu Contador Marçal';
const STORAGE_BIOMETRIA_ATIVA = 'cliente_biometria_ativa';
const STORAGE_BIOMETRIA_CRED_ID = 'cliente_biometria_cred_id'; // só web
const STORAGE_BIOMETRIA_CNPJ = 'cliente_biometria_cnpj';

// Detecta plataforma Capacitor em runtime. Sem import — usa o objeto
// global injetado pelo wrapper nativo. Em browser, retorna false.
function ehNativo() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
}

function getPluginNativo() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  return cap?.Plugins?.BiometricAuth || null;
}

// ---------- helpers WebAuthn (browser) ----------

function base64UrlToBuffer(b64) {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/').padEnd(b64.length + (4 - b64.length % 4) % 4, '=');
  const bin = atob(padded);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufferToBase64Url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function webAuthnSuportado() {
  if (typeof window === 'undefined') return false;
  return !!(window.PublicKeyCredential && navigator.credentials?.create);
}

// ---------- API pública ----------

export function isBiometriaSuportada() {
  return ehNativo() ? !!getPluginNativo() : webAuthnSuportado();
}

export async function temAutenticadorPlataforma() {
  if (ehNativo()) {
    const BA = getPluginNativo();
    if (!BA) return false;
    try {
      const r = await BA.checkBiometry();
      return !!r?.isAvailable;
    } catch { return false; }
  }
  if (!webAuthnSuportado()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { return false; }
}

export function biometriaAtivaPara(cnpj) {
  const cnpjLimpo = String(cnpj || '').replace(/\D/g, '');
  const ativa = localStorage.getItem(STORAGE_BIOMETRIA_ATIVA) === '1';
  const cnpjSalvo = localStorage.getItem(STORAGE_BIOMETRIA_CNPJ);
  return ativa && cnpjLimpo === cnpjSalvo;
}

export function biometriaAtivaQualquer() {
  return localStorage.getItem(STORAGE_BIOMETRIA_ATIVA) === '1';
}

export function cnpjDaBiometria() {
  return localStorage.getItem(STORAGE_BIOMETRIA_CNPJ) || null;
}

export async function ativarBiometria({ cnpj, nome }) {
  const cnpjLimpo = String(cnpj || '').replace(/\D/g, '');
  if (!cnpjLimpo) throw new Error('CNPJ inválido');

  if (ehNativo()) {
    const BA = getPluginNativo();
    if (!BA) throw new Error('biometria nativa não disponível');
    // Prompt nativo pra confirmar antes de armazenar a flag.
    await BA.authenticate({
      reason: `Ative o desbloqueio biométrico do ${_nomeApp()}`,
      cancelTitle: 'Agora não',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Usar senha do dispositivo',
      androidTitle: _nomeApp(),
      androidSubtitle: nome || cnpjLimpo,
      androidConfirmationRequired: false,
    });
    localStorage.setItem(STORAGE_BIOMETRIA_ATIVA, '1');
    localStorage.setItem(STORAGE_BIOMETRIA_CNPJ, cnpjLimpo);
    return { modo: 'nativo' };
  }

  if (!webAuthnSuportado()) throw new Error('biometria não suportada neste navegador');

  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: _nomeApp() },
      user: {
        id: new TextEncoder().encode(cnpjLimpo),
        name: cnpjLimpo,
        displayName: nome || cnpjLimpo,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60000,
      attestation: 'none',
    },
  });

  if (!credential) throw new Error('autorização cancelada');

  const credId = bufferToBase64Url(credential.rawId);
  localStorage.setItem(STORAGE_BIOMETRIA_ATIVA, '1');
  localStorage.setItem(STORAGE_BIOMETRIA_CRED_ID, credId);
  localStorage.setItem(STORAGE_BIOMETRIA_CNPJ, cnpjLimpo);
  return { modo: 'webauthn', credId };
}

export async function verificarBiometria() {
  if (localStorage.getItem(STORAGE_BIOMETRIA_ATIVA) !== '1') return false;

  if (ehNativo()) {
    const BA = getPluginNativo();
    if (!BA) return false;
    try {
      await BA.authenticate({
        reason: `Desbloqueie o ${_nomeApp()}`,
        cancelTitle: 'Cancelar',
        allowDeviceCredential: true,
        iosFallbackTitle: 'Usar senha do dispositivo',
        androidTitle: _nomeApp(),
        androidSubtitle: 'Use sua biometria pra entrar',
        androidConfirmationRequired: false,
      });
      return true;
    } catch { return false; }
  }

  if (!webAuthnSuportado()) return false;
  const credId = localStorage.getItem(STORAGE_BIOMETRIA_CRED_ID);
  if (!credId) return false;

  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);

  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{
          type: 'public-key',
          id: base64UrlToBuffer(credId),
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      },
    });
    return !!assertion;
  } catch { return false; }
}

export function desativarBiometria() {
  localStorage.removeItem(STORAGE_BIOMETRIA_ATIVA);
  localStorage.removeItem(STORAGE_BIOMETRIA_CRED_ID);
  localStorage.removeItem(STORAGE_BIOMETRIA_CNPJ);
}
